import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Typography, Dropdown, Menu, Space, Select, Divider, Row, Col } from 'antd';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { Construction, Copy, Delete, Edit, Ellipsis, Revert, Touch, Document } from 'assets/icons';
import Card from './Card';
import StatusCard from './StatusCard';
import dayjs from 'dayjs';

const menuIconStyle = { style: { fontSize: 20 } };

const MemoizedTags = React.memo(({ config, models }) => {
	if (!config) {
		return null;
	}

	const renderConfigTag = (key, value, index) => {
		switch (key) {
			case 'hardware':
				return (
					value && (
						<StatusCard status="edge" key={index}>
							{value.toUpperCase()}
						</StatusCard>
					)
				);
			case 'database':
			case 'visualization':
				return (
					value === true && (
						<StatusCard status="edge" key={index}>
							{key}
						</StatusCard>
					)
				);
			case 'protocol':
				return (
					value.length > 0 && (
						<StatusCard status="edge" key={index}>
							{value.map((item, idx) => [idx ? ', ' : null, item])}
						</StatusCard>
					)
				);
			case 'mlBox':
				return value.map((item, idx) => {
					const result = models.find(({ modelid }) => modelid === item);
					return (
						result && (
							<StatusCard status="edge" key={idx}>
								{result.name}
							</StatusCard>
						)
					);
				});
			default:
				return null;
		}
	};

	return <>{Object.keys(config).map((key, index) => renderConfigTag(key, config[key], index))}</>;
});

const EdgeListCard = ({
	item,
	models,
	onEdit,
	onConfig,
	onDelete,
	onUndo,
	onSelectDevice,
	onDuplicate,
	onOpenFlowEditor,
}) => {
	const [ver, setVer] = useState('latest');
	const [config, setConfig] = useState(() => item.recipeconfig.find((config) => config.version === 'latest'));
	const [connectionStatus, setConnectionStatus] = useState(item.connect_status);
	const history = useHistory();

	useEffect(() => {
		const selectedConfig = item.recipeconfig.find((config) => config.version === ver);
		setConfig(selectedConfig);
	}, [ver, item]);

	const toDeviceList = useCallback(() => {
		history.push({ pathname: `${history.location.pathname}/${item.recipeid}` });
	}, [history, item.recipeid]);

	const menu = useMemo(
		() => (
			<StyledMenu>
				<Menu.Item key="duplicate-btn" icon={<Copy {...menuIconStyle} />} onClick={onDuplicate}>
					Duplicate
				</Menu.Item>
				<Menu.Item key="configure-btn" icon={<Construction {...menuIconStyle} />} onClick={onConfig}>
					Configure
				</Menu.Item>
				<Menu.Item key="edit-btn" icon={<Edit {...menuIconStyle} />} onClick={onEdit}>
					Edit
				</Menu.Item>
				<Menu.Item key="flow-editor-btn" icon={<Document {...menuIconStyle} />} onClick={onOpenFlowEditor}>
					Flow Editor
				</Menu.Item>
				<Menu.Item key="choose-device-btn" icon={<Touch {...menuIconStyle} />} onClick={onSelectDevice}>
					Choose device
				</Menu.Item>
				<Menu.Divider />
				<Menu.Item
					key="revert-btn"
					icon={<Revert {...menuIconStyle} />}
					onClick={onUndo}
					disabled={item.recipeconfig.length < 2}
				>
					Revert to previous version
				</Menu.Item>
				<Menu.Divider />
				<Menu.Item key="delete-btn" icon={<Delete {...menuIconStyle} />} danger onClick={onDelete}>
					Delete
				</Menu.Item>
			</StyledMenu>
		),
		[onDuplicate, onConfig, onEdit, onOpenFlowEditor, onSelectDevice, onUndo, onDelete, item.recipeconfig.length]
	);

	return (
		<Card onClick={toDeviceList}>
			<div>
				<TopCard>
					<NameEdge>{item.name}</NameEdge>
					<Space>
						<Dropdown
							overlay={menu}
							trigger={['click']}
							placement="bottomRight"
							disabled={ver === 'previous'}
						>
							<Button
								onClick={(e) => e.stopPropagation()}
								type="text"
								icon={<Ellipsis style={{ fontSize: 20 }} />}
								style={{ height: '32px', width: '32px' }}
							/>
						</Dropdown>
					</Space>
				</TopCard>
				<DescEdge>{item.description || ''}</DescEdge>
				<MetaInfo>
					<Row>
						<Col span={24}>
							<span className="label">Edge Recipe:</span>
							<span className="value">{item.recipeid || 'None'}</span>
						</Col>
					</Row>
					<Row>
						<Col span={24}>
							<span className="label">Last Updated:</span>
							<span className="value">
								{item.last_updated ? dayjs(item.last_updated).format('D MMMM YYYY h:mm A') : 'None'}
							</span>
						</Col>
					</Row>
					<Row align="middle" gutter={8}>
						<Col>
							<span className="label">Version:</span>
						</Col>
						<Col flex="auto">
							<Select
								onChange={setVer}
								value={ver}
								disabled={item.recipeconfig.length < 2}
								onClick={(e) => e.stopPropagation()}
								style={{ width: '100%' }}
							>
								<Select.Option value="previous">Previous</Select.Option>
								<Select.Option value="latest">Latest</Select.Option>
							</Select>
						</Col>
						<Col>
							<ConnectionStatusIndicator status={connectionStatus} />
						</Col>
					</Row>
				</MetaInfo>
				<DividerCard />
				<CardEdge>
					<MemoizedTags config={config} models={models} />
				</CardEdge>
			</div>
		</Card>
	);
};

export default React.memo(EdgeListCard);

const NameEdge = styled.div`
	font-size: 16px;
	font-weight: 500;
	line-height: 1.25;
	letter-spacing: normal;
	color: #333333;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	flex: 1;
	margin-right: 8px;
`;

const DescEdge = styled(Typography.Paragraph).attrs({
	ellipsis: {
		rows: 3,
	},
})`
	margin-top: 0px;
	font-size: 14px;
	font-weight: 300;
	line-height: 1.5;
	letter-spacing: normal;
	color: #4d4d4d;
	word-break: break-all;
	min-height: 63px;
`;

const TopCard = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
`;

const DividerCard = styled(Divider)`
	margin-top: 10px;
	margin-bottom: 0px;
`;

const CardEdge = styled(Space).attrs({ wrap: true, align: 'baseline', size: 2 })`
	width: 100% !important;
	margin-bottom: 8px;
	margin-top: 8px;
	min-height: 24px;
`;

const MetaInfo = styled.div`
	margin-top: 15px;
	font-size: 12px;

	.label {
		color: #808080;
		margin-right: 8px;
		font-weight: 300;
	}

	.value {
		color: #4d4d4d;
		word-break: break-all;
		font-weight: 400;
	}

	.ant-row {
		margin-bottom: 8px;
		&:last-child {
			margin-bottom: 0;
		}
	}
`;

const StyledMenu = styled(Menu)`
	.ant-dropdown-menu-item,
	.ant-dropdown-menu-submenu-title {
		font-size: 14px;
		font-weight: normal;
		line-height: 1.5;
		letter-spacing: normal;
		color: #4d4d4d;
		padding-left: 20px;
	}
	.ant-dropdown-menu-item-disabled {
		background-color: rgba(230, 230, 230, 0.8) !important;
	}

	min-width: 180px;
	padding: 8px 0;
	border-radius: 5px;
	box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.2);
	background-color: #ffffff;

	.ant-dropdown-menu-item-divider,
	.ant-dropdown-menu-submenu-title-divider {
		background-color: rgba(207, 218, 230, 0.5);
	}
	.ant-dropdown-menu-item:hover,
	.ant-dropdown-menu-submenu-title:hover {
		background-color: #fff8e8;
	}

	.ant-dropdown-menu-item.ant-dropdown-menu-item-danger {
		:hover {
			color: #f64e60;
			background-color: rgba(246, 78, 96, 0.14);
			font-weight: normal;
		}
	}
`;

const ConnectionStatusIndicator = styled.div`
	width: 12px;
	height: 12px;
	border-radius: 50%;
	background-color: ${(props) => (props.status ? '#52c41a' : 'lightgray')};
	margin-left: 8px;
`;
